import React, { useState, useEffect, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Users2, Plus, Pencil, Trash2, ArrowUp, ArrowDown, Minus, GripVertical, ShieldCheck, BellRing, UserPlus, Info, Archive, Upload, X, Camera } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useFacility } from "@/context/FacilityContext";
import NotificationPrefs from "@/components/NotificationPrefs";
import { resizeAvatarToDataUrl } from "@/lib/avatar";

// Default onboarding password for the "Add employee" form. Overridable via
// `REACT_APP_DEFAULT_EMPLOYEE_PASSWORD` at build-time so operators can
// swap it without patching the source. Admins should force employees to
// reset on first sign-in anyway.
const DEFAULT_EMPLOYEE_PW = process.env.REACT_APP_DEFAULT_EMPLOYEE_PASSWORD || "employee123";

const EMPTY = { name: "", email: "", role: "employee", role_label: "", phone: "",
                departments_trained: [], avatar: "", password: DEFAULT_EMPLOYEE_PW,
                facility_ids: [], roster_facility_ids: [], primary_facility_id: null,
                availability_required: false,
                nso_participant: false, hire_date: "" };

const ROLE_BADGES = {
  admin:    { label: "Admin",    cls: "bg-amber-100 text-amber-800 border-amber-300" },
  manager:  { label: "Manager",  cls: "bg-blue-100 text-blue-800 border-blue-300" },
  employee: { label: "Employee", cls: "bg-slate-100 text-slate-700 border-slate-300" },
};

export default function EmployeesPage({ employees, onChange, facilityId }) {
  const { user: me } = useAuth();
  const [open, setOpen]     = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]     = useState(EMPTY);
  const [departments, setDepartments] = useState([]);
  const [roster, setRoster] = useState([]);
  const [archived, setArchived] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [sepDialog, setSepDialog] = useState({ open: false, afterIndex: -1 });
  const [sepLabel, setSepLabel] = useState("");

  const isAdmin = me?.role === "admin";

  const [facilities, setFacilities] = useState([]);
  const [deptMgrOpen, setDeptMgrOpen] = useState(false);

  // ---- Email lookup for the New Employee dialog ----
  // Debounced: whenever the user finishes typing an email, ask the backend if
  // it already belongs to someone.  If so, we surface an "Import to this
  // facility" banner instead of the old red duplicate error.
  const [emailMatch, setEmailMatch] = useState(null);   // { user } | null
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!open || editing) { setEmailMatch(null); return; }
    const e = (form.email || "").trim().toLowerCase();
    if (!e || !e.includes("@")) { setEmailMatch(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/employees/lookup?email=${encodeURIComponent(e)}`);
        if (!cancelled) setEmailMatch(data?.exists ? { user: data.user } : null);
      } catch { if (!cancelled) setEmailMatch(null); }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.email, open, editing]);

  const alreadyOnThisFacility = emailMatch?.user
    && (emailMatch.user.roster_facility_ids || emailMatch.user.facility_ids || []).includes(facilityId);

  const importExisting = async () => {
    if (!emailMatch?.user) return;
    setImporting(true);
    try {
      await api.post("/employees/import", {
        user_id: emailMatch.user.id, facility_id: facilityId,
        add_to_roster: true, unarchive: true,
      });
      toast.success(`${emailMatch.user.name} imported to this facility`);
      setOpen(false);
      loadRoster();
      onChange && onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Import failed");
    }
    setImporting(false);
  };

  const loadDepts = async () => {
    try {
      const url = facilityId ? `/meta/departments?facility_id=${encodeURIComponent(facilityId)}` : "/meta/departments";
      const { data } = await api.get(url); setDepartments(data);
    } catch { /* ignore */ }
  };
  const loadFacilities = async () => {
    try { const { data } = await api.get("/facilities"); setFacilities(data); } catch { /* ignore */ }
  };
  const loadRoster = async () => {
    if (!facilityId) return;
    try { const { data } = await api.get(`/roster?facility_id=${facilityId}`); setRoster(data); } catch { /* ignore */ }
  };
  const loadArchived = async () => {
    if (!facilityId) return;
    try { const { data } = await api.get(`/archived-employees?facility_id=${facilityId}`); setArchived(data); } catch { setArchived([]); }
  };
  useEffect(() => { loadDepts(); loadFacilities(); /* eslint-disable-next-line */ }, [facilityId]);
  // Only reload the roster on facility change. The `employees` prop used to be
  // a dependency here, but that caused every save (or parent refetch) to
  // clobber the local optimistic order and made rows appear to "jump" after
  // routine edits.
  useEffect(() => { loadRoster(); loadArchived(); }, [facilityId]);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, facility_ids: [facilityId], roster_facility_ids: [facilityId], primary_facility_id: facilityId }); setOpen(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      name: e.name || "",
      email: e.email || "",
      role: e.role || "employee",
      role_label: e.role_label || e.position || "",
      phone: e.phone || "",
      departments_trained: e.departments_trained || [],
      avatar: e.avatar || "",
      password: "",
      facility_ids: e.facility_ids || [],
      roster_facility_ids: e.roster_facility_ids || e.facility_ids || [],
      primary_facility_id: e.primary_facility_id || null,
      availability_required: !!e.availability_required,
      nso_participant: !!e.nso_participant,
      hire_date: e.hire_date || "",
    });
    setOpen(true);
  };

  const toggleFacility = (fid, field) => {
    setForm((f) => {
      const has = f[field].includes(fid);
      const nextList = has ? f[field].filter((x) => x !== fid) : [...f[field], fid];
      const next = { ...f, [field]: nextList };
      // Primary bookkeeping — only relevant when we're editing the
      // `facility_ids` list (not the roster list). Keep the invariant
      // "primary must be one of facility_ids" enforced in the UI so the
      // Save button never bounces on a 400 from the API.
      if (field === "facility_ids" && f.role === "employee") {
        if (has) {
          // Just removed a facility. If it was the primary, promote the
          // next-remaining one (preserve first-added-wins order).
          if (f.primary_facility_id === fid) {
            next.primary_facility_id = nextList[0] || null;
          }
        } else {
          // Adding a facility. If there's no primary yet, this one is it.
          if (!f.primary_facility_id) next.primary_facility_id = fid;
        }
      }
      return next;
    });
  };

  const setPrimaryFacility = (fid) => {
    setForm((f) => {
      if (!f.facility_ids.includes(fid)) return f;
      return { ...f, primary_facility_id: fid };
    });
  };

  const toggleDept = (d) => {
    setForm((f) => f.departments_trained.includes(d)
      ? { ...f, departments_trained: f.departments_trained.filter((x) => x !== d) }
      : { ...f, departments_trained: [...f.departments_trained, d] });
  };

  const save = async () => {
    try {
      // For NEW employees an empty facility array means "add them to the current
      // facility" (sensible default).  For EDITS, respect the user's intent —
      // an empty array means "remove them from every roster / facility".
      const facs =
        !editing && !(form.facility_ids?.length)          ? [facilityId] :
        form.facility_ids || [];
      const roster =
        !editing && !(form.roster_facility_ids?.length)   ? [facilityId] :
        (form.roster_facility_ids ?? []);
      const payload = {
        name: form.name, email: form.email, role_label: form.role_label,
        phone: form.phone || null, departments_trained: form.departments_trained,
        avatar: form.avatar || null,
        facility_ids: facs,
        roster_facility_ids: roster,
        primary_facility_id: form.role === "employee"
          ? (form.primary_facility_id && facs.includes(form.primary_facility_id)
              ? form.primary_facility_id
              : (facs[0] || null))
          : null,
        availability_required: !!form.availability_required,
        nso_participant: !!form.nso_participant,
        hire_date: form.hire_date || null,
      };
      if (isAdmin) payload.role = form.role || "employee";
      // Managers can flip employee ↔ manager (but never admin). Only send
      // the role if it actually changed and the new value is safe.
      else if (editing && form.role && form.role !== editing.role && form.role !== "admin" && editing.role !== "admin") {
        payload.role = form.role;
      } else if (!editing && form.role && form.role !== "admin") {
        payload.role = form.role;
      }
      if (form.password) payload.password = form.password;
      if (editing) {
        await api.patch(`/employees/${editing.id}`, payload);
        toast.success("Employee updated");
      } else {
        await api.post("/employees", payload);
        toast.success("Employee added");
      }
      setOpen(false);
      // Refresh the LOCAL roster list so the edit/add appears immediately
      // (the `employees` prop from Dashboard is a different endpoint and
      // isn't what this page renders).
      loadRoster();
      onChange && onChange();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "email_exists_active") {
        setEmailMatch({ user: detail.user });
        toast.error("Email already in use — use the Import banner below to add them here");
        return;
      }
      toast.error(formatApiErrorDetail(detail) || "Failed");
    }
  };

  const remove = async (emp) => {
    if (!window.confirm(`Archive ${emp.name}? Their schedule history is kept and you can reactivate them anytime from the Archived section.`)) return;
    try {
      await api.delete(`/employees/${emp.id}`);
      toast.success("Employee archived");
      loadRoster();
      loadArchived();
      onChange && onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
  };

  const reactivate = async (emp) => {
    try {
      await api.post(`/employees/${emp.id}/reactivate`);
      toast.success(`${emp.name} reactivated`);
      loadRoster();
      loadArchived();
      onChange && onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
  };

  const purge = async (emp) => {
    // Two-tap confirmation — first prompt explains what's about to happen,
    // second prompt requires the manager to type the employee's name back.
    if (!window.confirm(
      `⚠ PERMANENT DELETE — ${emp.name}\n\n` +
      `This will wipe the employee AND every trace of them:\n` +
      `• all past & future shift assignments\n` +
      `• availability submissions and history\n` +
      `• PTO, trade, pickup, call-out, mod requests\n` +
      `• Shift Farm XP, coins, ledger, trade ads\n` +
      `• notifications & sessions\n\n` +
      `This cannot be undone. Reactivate instead if you might need them back.\n\n` +
      `Continue?`)) return;
    const typed = window.prompt(`Type the employee's full name to confirm:\n\n${emp.name}`);
    if ((typed || "").trim().toLowerCase() !== (emp.name || "").trim().toLowerCase()) {
      toast.error("Name didn't match — purge cancelled.");
      return;
    }
    try {
      const { data } = await api.delete(`/employees/${emp.id}/purge`);
      const totals = data?.wiped_counts || {};
      const trail = Object.entries(totals).map(([k, v]) => `${k}:${v}`).join(", ") || "no historical rows";
      toast.success(`Purged ${emp.name} — wiped ${trail}`);
      loadArchived();
      onChange && onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Purge failed"); }
  };

  // Optimistic reorder: update the local state immediately so the UI is
  // instant, then background-persist. If the save fails we roll back.
  const debouncePersist = React.useRef(null);
  const persistOrder = (nextRoster, { savePrev } = {}) => {
    const prev = roster;
    setRoster(nextRoster);
    if (debouncePersist.current) clearTimeout(debouncePersist.current);
    debouncePersist.current = setTimeout(async () => {
      const items = nextRoster.map((it) => it.type === "employee"
        ? { type: "employee", id: it.id }
        : { type: "separator", id: it.id, label: it.label });
      try {
        await api.put("/roster/order", { facility_id: facilityId, items });
      } catch (e) {
        const detail = formatApiErrorDetail(e?.response?.data?.detail)
          || e?.response?.statusText
          || e?.message
          || "unknown error";
        // eslint-disable-next-line no-console
        console.error("roster/order failed:", e?.response?.status, detail, e);
        toast.error(`Failed to save order — ${detail}`);
        setRoster(savePrev || prev);
      }
    }, 250);
  };

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= roster.length) return;
    const next = [...roster];
    [next[index], next[target]] = [next[target], next[index]];
    persistOrder(next);
  };

  // ── Drag-and-drop (HTML5 native, no lib) ──
  const [dragIdx, setDragIdx] = useState(null);
  const [dropIdx, setDropIdx] = useState(null);

  // While dragging, auto-scroll the page when the pointer nears the top or
  // bottom edge of the viewport — no more manual drag-and-drop across long
  // rosters. Uses a rAF loop keyed on the last known drag Y-coordinate.
  const dragScrollRef = React.useRef({ y: 0, active: false, raf: 0 });
  React.useEffect(() => {
    if (dragIdx == null) return;
    const state = dragScrollRef.current;
    state.active = true;
    const EDGE = 90;        // px from top/bottom that triggers scroll
    const MAX_SPEED = 24;   // px per frame at the very edge
    const onDragMove = (e) => { state.y = e.clientY; };
    const tick = () => {
      if (!state.active) return;
      const vh = window.innerHeight;
      const y = state.y;
      let dy = 0;
      if (y < EDGE)          dy = -Math.round(((EDGE - y) / EDGE) * MAX_SPEED);
      else if (y > vh - EDGE) dy =  Math.round(((y - (vh - EDGE)) / EDGE) * MAX_SPEED);
      if (dy !== 0) window.scrollBy(0, dy);
      state.raf = requestAnimationFrame(tick);
    };
    window.addEventListener("dragover", onDragMove);
    state.raf = requestAnimationFrame(tick);
    return () => {
      state.active = false;
      cancelAnimationFrame(state.raf);
      window.removeEventListener("dragover", onDragMove);
    };
  }, [dragIdx]);

  const onDragStart = (idx) => (e) => {
    setDragIdx(idx);
    try { e.dataTransfer.setData("text/plain", String(idx)); } catch {}
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropIdx(idx);
  };
  const onDrop = (idx) => (e) => {
    e.preventDefault();
    const from = dragIdx;
    setDragIdx(null); setDropIdx(null);
    if (from == null || from === idx) return;
    const next = [...roster];
    const [moved] = next.splice(from, 1);
    next.splice(idx > from ? idx - 1 : idx, 0, moved);
    persistOrder(next);
  };
  const onDragEnd = () => { setDragIdx(null); setDropIdx(null); };

  const addSeparator = () => {
    const at = sepDialog.afterIndex;
    const next = [...roster];
    next.splice(at + 1, 0, { type: "separator", id: `tmp-${Date.now()}`, label: sepLabel || "Section", position: 0 });
    persistOrder(next);
    setSepDialog({ open: false, afterIndex: -1 }); setSepLabel("");
  };
  const removeSeparator = (id) => {
    const next = roster.filter((it) => !(it.type === "separator" && it.id === id));
    persistOrder(next);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white" data-testid="employees-page">
      <div className="p-5 border-b border-slate-200 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-lg font-display font-semibold">
            <Users2 size={18} className="text-blue-600" /> Employees
          </div>
          <div className="text-sm text-slate-500">Drag the ⋮⋮ handle on the left to reorder, or use the ↑↓ arrows. Drop in section headers, pick roles and departments trained.</div>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={openNew} data-testid="emp-add">
          <Plus size={14} className="mr-1" /> New employee
        </Button>
      </div>

      <div className="divide-y divide-slate-100">
        {roster.length === 0 && <div className="p-6 text-sm text-slate-500">No employees yet.</div>}
        {roster.map((it, idx) => {
          if (it.type === "separator") {
            return (
              <div key={it.id} className="px-5 py-2.5 bg-slate-50 flex items-center gap-3" data-testid={`sep-${it.id}`}>
                <div className="h-px bg-slate-300 flex-1" />
                <span className="text-xs uppercase tracking-widest text-slate-600 font-semibold">{it.label}</span>
                <div className="h-px bg-slate-300 flex-1" />
                {isAdmin && (
                  <>
                    <button onClick={() => move(idx, -1)} className="text-slate-400 hover:text-blue-600" aria-label="Up"><ArrowUp size={14} /></button>
                    <button onClick={() => move(idx, +1)} className="text-slate-400 hover:text-blue-600" aria-label="Down"><ArrowDown size={14} /></button>
                    <button onClick={() => removeSeparator(it.id)} className="text-slate-400 hover:text-red-600" aria-label="Remove separator"><Minus size={14} /></button>
                  </>
                )}
              </div>
            );
          }
          const e = it;
          const rb = ROLE_BADGES[e.role] || ROLE_BADGES.employee;
          return (
            <div
              key={e.id}
              className={`p-4 flex items-center gap-3 transition-colors ${
                dragIdx === idx ? "opacity-40" : ""
              } ${dropIdx === idx && dragIdx !== null && dragIdx !== idx ? "border-t-2 border-t-blue-500" : ""}`}
              draggable
              onDragStart={onDragStart(idx)}
              onDragOver={onDragOver(idx)}
              onDrop={onDrop(idx)}
              onDragEnd={onDragEnd}
              data-testid={`emp-row-${e.id}`}
            >
              <div
                className="flex flex-col items-center gap-0.5 text-slate-300 cursor-grab active:cursor-grabbing select-none touch-none"
                title="Drag to reorder"
                data-testid={`emp-drag-${e.id}`}
              >
                <GripVertical size={14} className="text-slate-400" />
                <button onClick={() => move(idx, -1)} className="hover:text-blue-600" aria-label="Up" data-testid={`emp-up-${e.id}`}><ArrowUp size={11} /></button>
                <button onClick={() => move(idx, +1)} className="hover:text-blue-600" aria-label="Down" data-testid={`emp-down-${e.id}`}><ArrowDown size={11} /></button>
              </div>
              <Avatar className="w-10 h-10">
                <AvatarImage src={e.avatar} />
                <AvatarFallback>{e.name?.[0]}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate flex items-center gap-2">
                  {e.name}
                  <span className={`text-[10px] rounded-md border px-1.5 py-0.5 uppercase tracking-widest font-semibold ${rb.cls}`}>
                    {e.role === "admin" && <ShieldCheck size={9} className="inline mr-0.5" />}
                    {rb.label}
                  </span>
                  {typeof e.fte === "number" && (
                    <span className="tabular-nums rounded-md bg-blue-50 text-blue-700 px-1.5 py-0.5 text-[11px] font-semibold">FTE {e.fte.toFixed(1)}</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 truncate">{e.email}{e.phone ? ` · ${e.phone}` : ""}</div>
              </div>
              <div className="hidden md:block w-40 min-w-0">
                <div className="text-xs uppercase tracking-widest text-slate-400">Role label</div>
                <div className="text-sm truncate">{e.role_label || e.position || "—"}</div>
              </div>
              <div className="hidden md:block w-64 min-w-0">
                <div className="text-xs uppercase tracking-widest text-slate-400">Departments trained</div>
                <div className="text-sm truncate flex flex-wrap gap-1 mt-0.5">
                  {(e.departments_trained || []).length === 0
                    ? <span className="text-slate-400 text-xs">—</span>
                    : (e.departments_trained || []).map((d, i) => (
                      <span key={i} className="text-[11px] rounded-md bg-slate-100 border border-slate-200 px-1.5 py-0.5">{d}</span>
                    ))}
                </div>
              </div>
              {isAdmin && (
                <button onClick={() => setSepDialog({ open: true, afterIndex: idx })}
                  className="text-slate-400 hover:text-blue-600" aria-label="Add separator" data-testid={`emp-sep-add-${e.id}`}>
                  <GripVertical size={16} />
                </button>
              )}
              <button onClick={() => openEdit(e)} data-testid={`emp-edit-${e.id}`}
                className="text-slate-500 hover:text-blue-600" aria-label="Edit"><Pencil size={16} /></button>
              <button onClick={() => remove(e)} data-testid={`emp-delete-${e.id}`}
                className="text-slate-400 hover:text-red-600" aria-label="Delete"><Trash2 size={16} /></button>
            </div>
          );
        })}
      </div>

      {archived.length > 0 && (
        <div className="border-t border-slate-200" data-testid="archived-section">
          <button
            className="w-full px-5 py-3 flex items-center justify-between text-sm hover:bg-slate-50"
            onClick={() => setShowArchived(!showArchived)}
            data-testid="archived-toggle"
          >
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-widest font-semibold text-slate-500">Archived</span>
              <span className="rounded-full bg-slate-100 text-slate-700 text-[10px] font-semibold px-1.5 py-0.5 tabular-nums">
                {archived.length}
              </span>
            </div>
            <span className="text-xs text-slate-500">{showArchived ? "Hide" : "Show"}</span>
          </button>
          {showArchived && (
            <div className="divide-y divide-slate-100 bg-slate-50/40">
              {archived.map((e) => (
                <div key={e.id} className="p-4 flex items-center gap-3" data-testid={`archived-row-${e.id}`}>
                  <Avatar className="w-9 h-9 opacity-70">
                    <AvatarImage src={e.avatar} />
                    <AvatarFallback>{e.name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate text-slate-700">{e.name}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {e.email}
                      {e.archived_at && <> · archived {new Date(e.archived_at).toLocaleDateString("en-US")}</>}
                      {e.archived_by_name && <> by {e.archived_by_name}</>}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                    onClick={() => reactivate(e)}
                    data-testid={`archived-reactivate-${e.id}`}
                  >
                    Reactivate
                  </Button>
                  {isAdmin && (
                    <Button
                      variant="outline"
                      className="text-red-700 border-red-300 hover:bg-red-50"
                      onClick={() => purge(e)}
                      title="Permanently delete this employee and all of their history (admin only)"
                      data-testid={`archived-purge-${e.id}`}
                    >
                      Delete forever
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit employee" : "New employee"}</DialogTitle>
            <DialogDescription>Set role, contact info, and departments they&apos;re trained on.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Full name</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="emp-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="emp-email" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="emp-phone" placeholder="(209) 555-0123" />
              </div>
            </div>
            <div>
              <Label>Hire date <span className="text-slate-400 text-xs font-normal">— powers PTO accrual math</span></Label>
              <Input
                type="date"
                value={form.hire_date}
                onChange={e => setForm({ ...form, hire_date: e.target.value })}
                data-testid="emp-hire-date"
                className="tabular-nums"
              />
            </div>

            {/* Import banner — shown when the typed email matches an existing account */}
            {!editing && emailMatch?.user && (
              <div className={`rounded-xl border p-3 flex items-start gap-3 ${
                alreadyOnThisFacility ? "border-slate-200 bg-slate-50" :
                emailMatch.user.is_archived ? "border-amber-300 bg-amber-50" :
                "border-blue-300 bg-blue-50"
              }`} data-testid="emp-import-banner">
                <div className="shrink-0 w-8 h-8 rounded-full bg-white border border-slate-200 grid place-items-center">
                  {emailMatch.user.is_archived ? <Archive size={14} className="text-amber-700" /> : <Info size={14} className="text-blue-700" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800">
                    {emailMatch.user.name}
                    <span className="ml-1 text-xs font-normal text-slate-500">· {emailMatch.user.email}</span>
                  </div>
                  <div className="text-[11px] text-slate-600 mt-0.5">
                    {alreadyOnThisFacility
                      ? "This account is already on this facility's roster — you can find them in the list above."
                      : emailMatch.user.is_archived
                        ? "This account is archived. Importing will unarchive them and add them here. Their history stays put."
                        : `Active at ${(emailMatch.user.facility_ids || []).length} other facilit${(emailMatch.user.facility_ids || []).length === 1 ? "y" : "ies"} — import to add them to this one too.`}
                  </div>
                </div>
                {!alreadyOnThisFacility && (
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 shrink-0"
                    onClick={importExisting}
                    disabled={importing}
                    data-testid="emp-import-btn"
                  >
                    <UserPlus size={12} className="mr-1" />
                    {importing ? "Importing…" : "Import"}
                  </Button>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Role</Label>
                {isAdmin ? (
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="emp-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                ) : !editing || editing.role !== "admin" ? (
                  // Managers: create OR edit any non-admin, flipping employee ↔ manager.
                  // Never expose an "Admin" option, and never let them touch an admin account.
                  <Select value={form.role === "admin" ? "employee" : form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="emp-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="mt-1.5 text-sm rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-500">
                    Admin
                    <span className="text-xs ml-1">(admin required to change)</span>
                  </div>
                )}
              </div>
              <div>
                <Label>Role label / title</Label>
                <Input placeholder="e.g. Senior Nurse, Cashier…" value={form.role_label}
                  onChange={e => setForm({ ...form, role_label: e.target.value })} data-testid="emp-role" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Departments trained</Label>
                {isAdmin && (
                  <button type="button"
                    onClick={() => setDeptMgrOpen(true)}
                    data-testid="manage-departments-btn"
                    className="text-[11px] uppercase tracking-widest text-blue-600 hover:text-blue-800 font-semibold"
                  >Manage departments</button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-1.5 mt-2 rounded-md border border-slate-200 p-2 max-h-52 overflow-y-auto thin-scroll">
                {departments.map((d) => (
                  <label key={d} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                    <Checkbox
                      checked={form.departments_trained.includes(d)}
                      onCheckedChange={() => toggleDept(d)}
                      data-testid={`emp-dept-${d.replace(/\s+/g, '-').toLowerCase()}`}
                    />
                    <span>{d}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-3 flex items-center gap-3" data-testid="emp-avatar-block">
              <EmployeeAvatarEditor
                value={form.avatar || ""}
                name={form.name}
                onChange={(next) => setForm({ ...form, avatar: next })}
              />
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
              <label className="flex items-start gap-3 text-sm cursor-pointer">
                <Checkbox
                  checked={!!form.availability_required}
                  onCheckedChange={(v) => setForm({ ...form, availability_required: !!v })}
                  data-testid="emp-availability-required"
                  className="mt-0.5"
                />
                <div className="min-w-0">
                  <div className="font-medium text-slate-800">Require availability submission</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    When on, this employee sees the Availability tab and must submit which shift categories (Days / Evenings / Nights) they can work each day.  Managers will see them in the Availability roster and get flagged when they&apos;re late or overdue.
                  </div>
                </div>
              </label>
            </div>
            {(form.facility_ids || []).some((f) => f === "auburn" || f === "solano") && (
              <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3">
                <label className="flex items-start gap-3 text-sm cursor-pointer">
                  <Checkbox
                    checked={!!form.nso_participant}
                    onCheckedChange={(v) => setForm({ ...form, nso_participant: !!v })}
                    data-testid="emp-nso-participant"
                    className="mt-0.5"
                  />
                  <div className="min-w-0">
                    <div className="font-medium text-slate-800">Participates in New Schedule Open Shifts (Auburn / Solano only)</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      When on, this employee sees the <b>New Schedule Open Shifts</b> tab from the 1st to the 13th of each month and can request next-month open shifts. Highest-seniority non-OT request wins each slot at close (14th 00:00). OT-flagged requests stay for manager review.
                    </div>
                  </div>
                </label>
              </div>
            )}
            <>
                <div>
                  <Label>Facility access (can view / manage)</Label>
                  <div className="grid grid-cols-2 gap-1.5 mt-2 rounded-md border border-slate-200 p-2">
                    {facilities.map((f) => {
                      const canPick = isAdmin || (me?.facility_ids || []).includes(f.id);
                      const checked = form.facility_ids.includes(f.id);
                      const isPrimary = form.role === "employee" && checked && form.primary_facility_id === f.id;
                      const isSecondary = form.role === "employee" && checked && !!form.primary_facility_id && form.primary_facility_id !== f.id;
                      return (
                        <div
                          key={f.id}
                          className={`flex items-center gap-2 text-sm rounded px-1 py-0.5 ${canPick ? "hover:bg-slate-50" : "opacity-40 cursor-not-allowed"}`}
                          data-testid={`emp-fac-row-${f.id}`}
                        >
                          <label className={`flex items-center gap-2 flex-1 min-w-0 ${canPick ? "cursor-pointer" : "cursor-not-allowed"}`}>
                            <Checkbox
                              checked={checked}
                              disabled={!canPick}
                              onCheckedChange={() => canPick && toggleFacility(f.id, "facility_ids")}
                              data-testid={`emp-fac-${f.id}`}
                            />
                            <span className="truncate">{f.label}</span>
                          </label>
                          {isPrimary && (
                            <span
                              data-testid={`emp-fac-primary-${f.id}`}
                              className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-md border bg-fuchsia-100 border-fuchsia-300 text-fuchsia-800"
                              title="Primary — this is where they can submit PTO / availability / etc."
                            >
                              Primary
                            </span>
                          )}
                          {isSecondary && (
                            <button
                              type="button"
                              onClick={() => canPick && setPrimaryFacility(f.id)}
                              disabled={!canPick}
                              data-testid={`emp-fac-set-primary-${f.id}`}
                              className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-md border transition-colors ${
                                canPick
                                  ? "bg-slate-100 border-slate-300 text-slate-600 hover:bg-fuchsia-50 hover:border-fuchsia-300 hover:text-fuchsia-800"
                                  : "bg-slate-100 border-slate-300 text-slate-400"
                              }`}
                              title="Secondary — click to make this the primary facility"
                            >
                              Secondary
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    For employees, usually one facility. For managers, pick every site they can control.
                    {form.role === "employee" && (
                      <span className="block">
                        <b className="text-fuchsia-700">Primary</b> is the employee&apos;s home facility — where they submit PTO, availability, trades, mods, and call-outs.
                        On secondary facilities they can only view the schedule and pick up open shifts.
                        Click <b>Secondary</b> on any other facility to promote it.
                      </span>
                    )}
                    {!isAdmin && <span className="block text-[11px] text-slate-400 italic">You can only grant access to facilities you have yourself.</span>}
                  </div>
                </div>
                <div>
                  <Label>Show on schedule at (roster)</Label>
                  <div className="grid grid-cols-2 gap-1.5 mt-2 rounded-md border border-slate-200 p-2">
                    {facilities.map((f) => {
                      const accessible = form.facility_ids.includes(f.id);
                      const canPick    = isAdmin || (me?.facility_ids || []).includes(f.id);
                      const enabled    = accessible && canPick;
                      return (
                        <label key={f.id} className={`flex items-center gap-2 text-sm rounded px-1 py-0.5 ${enabled ? "cursor-pointer hover:bg-slate-50" : "opacity-40 cursor-not-allowed"}`}>
                          <Checkbox
                            checked={form.roster_facility_ids.includes(f.id)}
                            disabled={!enabled}
                            onCheckedChange={() => enabled && toggleFacility(f.id, "roster_facility_ids")}
                            data-testid={`emp-roster-${f.id}`}
                          />
                          <span>{f.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    Where this person&apos;s row shows up on the schedule grid. Check both Auburn and Roseville to put an employee on both schedules.
                  </div>
                </div>
              </>
            <div>
              <Label>{editing ? "New password (leave blank to keep)" : "Initial password"}</Label>
              <Input type="text" value={form.password || ""} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="emp-password" />
            </div>

            {editing?.id && (
              <div className="border border-slate-200 rounded-xl p-3" data-testid="emp-dialog-notif-prefs">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <BellRing size={14} className="text-blue-600" /> Notifications for {form.name?.split(" ")[0] || "this employee"}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Turn each event on/off for in-app and email. Overrides what they picked in their own "My Info" page.
                </div>
                <div className="mt-2">
                  <NotificationPrefs userId={editing.id} showManagerHint compact />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={save} data-testid="emp-save">
              {editing ? "Save changes" : "Add employee"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={sepDialog.open} onOpenChange={(v) => !v && setSepDialog({ open: false, afterIndex: -1 })}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Add section header</DialogTitle>
            <DialogDescription>Groups the employees below it. Works like a titled ribbon.</DialogDescription>
          </DialogHeader>
          <Input value={sepLabel} onChange={(e) => setSepLabel(e.target.value)} placeholder="e.g. Emergency Room" data-testid="sep-label" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSepDialog({ open: false, afterIndex: -1 })}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={addSeparator} data-testid="sep-save">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DepartmentManagerDialog
        open={deptMgrOpen}
        onClose={() => setDeptMgrOpen(false)}
        facilities={facilities}
        onChanged={loadDepts}
      />
    </div>
  );
}

/**
 * DepartmentManagerDialog — admin-only editor for the master department list.
 * Each department can be scoped to specific facilities (empty = all facilities).
 * All changes cascade to every user's departments_trained + every shift-code's
 * department field.
 */
function DepartmentManagerDialog({ open, onClose, facilities, onChanged }) {
  const [rows, setRows]       = useState([]);   // [{ id, name, facility_ids }]
  const [newName, setNewName] = useState("");
  const [newScope, setNewScope] = useState([]); // facility_ids for new dept
  const [busy, setBusy]       = useState(false);
  const [drafts, setDrafts]   = useState({});   // { [id]: { name, facility_ids } }

  const reload = async () => {
    try {
      const { data } = await api.get("/meta/departments/full");
      setRows(data || []);
      setDrafts({});
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load"); }
  };
  useEffect(() => { if (open) { reload(); setNewName(""); setNewScope([]); } /* eslint-disable-next-line */ }, [open]);

  const draftFor = (r) => drafts[r.id] || { name: r.name, facility_ids: r.facility_ids || [] };
  const setDraft = (r, patch) => setDrafts((s) => ({ ...s, [r.id]: { ...draftFor(r), ...patch } }));
  const clearDraft = (r) => setDrafts((s) => { const x = { ...s }; delete x[r.id]; return x; });
  const dirty = (r) => {
    const d = drafts[r.id];
    if (!d) return false;
    if ((d.name || "").trim() !== r.name) return true;
    const a = new Set(d.facility_ids || []);
    const b = new Set(r.facility_ids || []);
    if (a.size !== b.size) return true;
    for (const x of a) if (!b.has(x)) return true;
    return false;
  };

  const add = async () => {
    const n = (newName || "").trim();
    if (!n) return;
    setBusy(true);
    try {
      await api.post("/meta/departments", { name: n, facility_ids: newScope });
      setNewName(""); setNewScope([]);
      await reload();
      onChanged && await onChanged();
      toast.success(`Added "${n}"`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to add"); }
    setBusy(false);
  };
  const save = async (r) => {
    const d = draftFor(r);
    setBusy(true);
    try {
      await api.patch("/meta/departments", {
        old_name: r.name,
        new_name: (d.name || "").trim() || r.name,
        facility_ids: d.facility_ids || [],
      });
      await reload();
      onChanged && await onChanged();
      toast.success("Saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
    setBusy(false);
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}"? Any employee currently marked as trained on this will have it removed. This can't be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/meta/departments/${encodeURIComponent(r.name)}`);
      await reload();
      onChanged && await onChanged();
      toast.success(`Deleted "${r.name}"`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
    setBusy(false);
  };

  const toggleFacility = (setFn, current, fid) => {
    const s = new Set(current || []);
    if (s.has(fid)) s.delete(fid); else s.add(fid);
    setFn([...s]);
  };
  const toggleAll = (setFn, current) => {
    setFn((current || []).length === 0 ? facilities.map(f => f.id) : []);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="manage-departments-dialog">
        <DialogHeader>
          <DialogTitle>Manage departments</DialogTitle>
          <DialogDescription>Add, rename, remove, and scope departments to specific facilities. Leave the facility list empty for a department to show at every facility.</DialogDescription>
        </DialogHeader>

        <div className="mt-3 rounded-lg border border-slate-200 p-3 bg-slate-50">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5 font-bold">Add new</div>
          <Input
            placeholder="New department name…"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
            data-testid="new-department-input"
          />
          <div className="mt-2">
            <FacilityScopePicker
              facilities={facilities}
              value={newScope}
              onChange={setNewScope}
              testId="new-dept-facilities"
            />
          </div>
          <div className="flex justify-end mt-2">
            <Button onClick={add} disabled={busy || !newName.trim()} className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="new-department-add">Add department</Button>
          </div>
        </div>

        <div className="mt-3 rounded-lg border border-slate-200 divide-y divide-slate-100">
          {rows.length === 0 && (
            <div className="p-3 text-xs text-slate-500 italic">No departments yet.</div>
          )}
          {rows.map((r) => {
            const d = draftFor(r);
            return (
              <div key={r.id} className="p-2.5" data-testid={`dept-row-${r.name.replace(/\s+/g,'-').toLowerCase()}`}>
                <div className="flex items-center gap-2">
                  <Input
                    value={d.name}
                    onChange={(e) => setDraft(r, { name: e.target.value })}
                    className="flex-1"
                  />
                  {dirty(r) ? (
                    <>
                      <Button size="sm" variant="outline" onClick={() => clearDraft(r)}>Reset</Button>
                      <Button size="sm" onClick={() => save(r)} disabled={busy} className="bg-blue-600 hover:bg-blue-700">Save</Button>
                    </>
                  ) : (
                    <Button size="sm" variant="ghost"
                      onClick={() => remove(r)}
                      disabled={busy}
                      className="text-rose-600 hover:text-rose-800 hover:bg-rose-50"
                      data-testid={`dept-delete-${r.name.replace(/\s+/g,'-').toLowerCase()}`}>
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
                <div className="mt-2">
                  <FacilityScopePicker
                    facilities={facilities}
                    value={d.facility_ids}
                    onChange={(next) => setDraft(r, { facility_ids: next })}
                    onToggleAll={() => setDraft(r, { facility_ids: (d.facility_ids || []).length === 0 ? facilities.map(f => f.id) : [] })}
                    testId={`dept-fac-${r.name.replace(/\s+/g,'-').toLowerCase()}`}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FacilityScopePicker({ facilities, value, onChange, testId }) {
  const all = (value || []).length === 0;
  return (
    <div className="rounded-md border border-slate-200 bg-white p-2">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Facilities</span>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
          <Checkbox
            checked={all}
            onCheckedChange={(v) => onChange(v ? [] : facilities.map(f => f.id))}
            data-testid={`${testId}-all`}
          />
          <span>{all ? "All facilities" : "Only:"}</span>
        </label>
      </div>
      {!all && (
        <div className="grid grid-cols-2 gap-1.5">
          {(facilities || []).map((f) => {
            const on = (value || []).includes(f.id);
            return (
              <label key={f.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                <Checkbox
                  checked={on}
                  onCheckedChange={() => {
                    const s = new Set(value || []);
                    if (s.has(f.id)) s.delete(f.id); else s.add(f.id);
                    onChange([...s]);
                  }}
                  data-testid={`${testId}-${f.id}`}
                />
                <span>{f.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// EmployeeAvatarEditor — replaces the old free-text "Avatar URL" input with a
// click-to-view / delete / upload UI so managers stop pasting broken URLs and
// employees stop showing up stretched.
function EmployeeAvatarEditor({ value, name, onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [viewer, setViewer] = useState(false);

  const initials = (name || "?").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "?";

  const pickFile = () => fileRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // let the same file be re-picked later
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("That file isn't an image"); return; }
    setBusy(true);
    try {
      const url = await resizeAvatarToDataUrl(f);
      onChange(url);
      toast.success("Photo updated — remember to save.");
    } catch (err) {
      toast.error("Couldn't load that image");
    } finally { setBusy(false); }
  };

  const remove = () => {
    if (!value) return;
    onChange("");
    toast.success("Photo removed — save to keep the change.");
  };

  return (
    <>
      <button
        type="button"
        onClick={() => value ? setViewer(true) : pickFile()}
        title={value ? "Click to view full-size" : "Click to upload a photo"}
        className="relative shrink-0 group"
        data-testid="emp-avatar-preview"
      >
        <Avatar className="w-20 h-20 ring-2 ring-white shadow-sm">
          <AvatarImage src={value || undefined} alt={name || ""} />
          <AvatarFallback className="text-base font-bold text-slate-500 bg-slate-100">
            {initials}
          </AvatarFallback>
        </Avatar>
        {!value && (
          <span className="absolute inset-0 rounded-full grid place-items-center bg-black/0 group-hover:bg-black/30 transition-colors">
            <Camera size={18} className="text-white opacity-0 group-hover:opacity-100" />
          </span>
        )}
      </button>

      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-800">Profile photo</div>
        <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">
          {value ? "Click the photo to view full-size — or use the buttons below." : "No photo yet. Upload one so this employee shows up on the schedule with a face."}
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={pickFile}
            disabled={busy}
            data-testid="emp-avatar-upload"
          >
            <Upload size={13} className="mr-1" /> {value ? "Upload new" : "Upload photo"}
          </Button>
          {value && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={remove}
              className="text-red-700 border-red-300 hover:bg-red-50"
              data-testid="emp-avatar-remove"
            >
              <X size={13} className="mr-1" /> Delete
            </Button>
          )}
        </div>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFile}
        data-testid="emp-avatar-file"
      />

      {/* Full-size viewer */}
      {viewer && value && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setViewer(false)}
          data-testid="emp-avatar-viewer"
        >
          <div className="max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={value}
              alt={name || ""}
              className="w-full max-h-[80vh] object-contain rounded-2xl bg-white shadow-2xl"
            />
            <div className="mt-3 flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setViewer(false); pickFile(); }} data-testid="emp-avatar-viewer-upload">
                <Upload size={13} className="mr-1" /> Upload new
              </Button>
              <Button variant="outline" className="text-red-700 border-red-300 hover:bg-red-50"
                      onClick={() => { setViewer(false); remove(); }} data-testid="emp-avatar-viewer-delete">
                <X size={13} className="mr-1" /> Delete
              </Button>
              <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={() => setViewer(false)} data-testid="emp-avatar-viewer-close">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
